import {
  DescriptionPage_ProductVariant as ProductVariant,
  DescriptionPage_ProfessionalCertificateType as ProfessionalCertificateType,
} from '__generated__/graphql-types';

import INDUSTRY_CERTIFICATION_EXAM_QUALIFIED_CERT_SLUGS from 'bundles/unified-description-page-common/constants/industryCertificationExamQualifiedSlugs';
import type { SpecializationFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCourseEntitiesFragments';

import _t from 'i18n!nls/xdp';

export const getCareerOutcomesTitleForProfessionalCertificate = (
  professionalCertificateType?: ProfessionalCertificateType | null,
  domainName?: string,
  jobTitle?: string | null
): string => {
  if (professionalCertificateType === ProfessionalCertificateType.Gateway && domainName) {
    return _t('Prepare for a career in #{domainName}', { domainName: jobTitle ?? domainName });
  }
  return _t('Advance your career with in-demand skills');
};

export const getCareerOutcomesTitleForSpecialization = (): string => {
  return _t('Advance your subject-matter expertise');
};

export const getCareerOutcomesTitleForCourse = (
  specializations: SpecializationFragment[],
  domainName?: string | null
): string => {
  if (
    specializations.length === 1 &&
    // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    specializations[0].productVariant === ProductVariant.ProfessionalCertificate &&
    domainName
  ) {
    return _t('Build your #{domainName} expertise', { domainName });
  }

  return _t('Build your subject-matter expertise');
};

export const getCareerOutcomesTitleForProject = (): string => {
  return _t('Learn, practice, and apply job-ready skills in less than 2 hours');
};

export const getCareerOutcomesValuePropsForProfessionalCertificate = ({
  slug,
  professionalCertificateType,
  partnerName,
  qualifyingJobTitles,
  additionalValueProps,
}: {
  slug: string;
  professionalCertificateType?: ProfessionalCertificateType | null;
  partnerName?: string;
  qualifyingJobTitles?: string[] | null;
  additionalValueProps?: string[] | null;
}): string[] | undefined => {
  if (!partnerName) {
    return undefined;
  }

  if (professionalCertificateType === ProfessionalCertificateType.Gateway) {
    return [
      _t('Receive professional-level training from #{partnerName}', { partnerName }),
      _t('Demonstrate your proficiency in portfolio-ready projects'),
      _t('Earn an employer-recognized certificate from #{partnerName}', { partnerName }),
      ...(qualifyingJobTitles
        ? [
            _t('Qualify for in-demand job titles: #{qualifyingJobTitles}', {
              qualifyingJobTitles: qualifyingJobTitles.join(_t(', ')),
            }),
          ]
        : []),
      ...(additionalValueProps ?? []),
    ];
  }

  return [
    _t('Receive professional-level training from #{partnerName}', { partnerName }),
    _t('Demonstrate your technical proficiency'),
    _t('Earn an employer-recognized certificate from #{partnerName}', { partnerName }),
    ...(INDUSTRY_CERTIFICATION_EXAM_QUALIFIED_CERT_SLUGS.includes(slug)
      ? [_t('Prepare for an industry certification exam')]
      : []),
    ...(additionalValueProps ?? []),
  ];
};

export const getCareerOutcomesValuePropsForSpecialization = (partnerName?: string): string[] => {
  return [
    _t('Learn in-demand skills from university and industry experts'),
    _t('Master a subject or tool with hands-on projects'),
    _t('Develop a deep understanding of key concepts'),
    ...(partnerName ? [_t('Earn a career certificate from #{partnerName}', { partnerName })] : []),
  ];
};

export const getCareerOutcomesValuePropsForCourse = (
  specializations?: SpecializationFragment[] | null,
  partnerName?: string
): string[] => {
  return [
    _t('Learn new concepts from industry experts '),
    _t('Gain a foundational understanding of a subject or tool'),
    _t('Develop job-relevant skills with hands-on projects'),
    ...(partnerName &&
    specializations &&
    specializations.length > 0 &&
    !specializations.some((specialization) => specialization.productVariant !== ProductVariant.ProfessionalCertificate)
      ? [_t('Earn a shareable career certificate from #{partnerName}', { partnerName })]
      : [_t('Earn a shareable career certificate')]),
  ];
};

export const getCareerOutcomesValuePropsForGuidedProject = (): string[] => {
  return [
    _t('Receive training from industry experts'),
    _t('Gain hands-on experience solving real-world job tasks'),
    _t('Build confidence using the latest tools and technologies'),
  ];
};

export const getCareerOutcomesValuePropsForProject = (): string[] => {
  return [_t('Receive training from industry experts'), _t('Gain hands-on experience solving real-world job tasks')];
};

export const getCareerOutcomesConsumerDisclaimers = () => {
  return _t('¹Based on Coursera learner outcome survey responses, United States, 2021.');
};

export const getCareerOutcomesEnterpriseDisclaimers = () => {
  return _t(
    '¹Career improvement (i.e. promotion, raise) based on Coursera learner outcome survey responses, United States, 2021.'
  );
};
